/**
 * Market-creation lifecycle builders. The on-chain creator field is the
 * user's wallet pubkey — every tx here MUST be signed by that wallet.
 *
 * The full lifecycle is three sequential transactions:
 *
 *   1. `buildCreateMarketShell` — allocates the Market account, the
 *      backer-share mint, share metadata, prize-pool vault, backer-fee vault,
 *      and platform-fee vault using the configured quote mint.
 *      Signers: `creator` plus optional rent `payer` when distinct.
 *   2. `buildInitOutcome` — called N times (N = `nOutcomes` from the
 *      shell), index 0..N-1 sequentially. Allocates per-outcome
 *      accounts: outcome state, bonding curve, outcome mint (a fresh
 *      keypair), claim-rights mint, BC token vault, BC quote vault.
 *      Signers: `creator`, optional rent `payer`, and the outcome-mint keypair. The
 *      outcome-mint Keypair is generated by the caller (orchestrator);
 *      this builder only takes its public key. Pre-signing with the
 *      keypair's secret happens OUTSIDE the SDK (build-only boundary).
 *   3. `buildFinalizeMarket` — transitions Market state from `Created`
 *      → `Backing` once every outcome is initialized. One signer:
 *      `creator`.
 *
 * The market PDA is derived from `[b"market", creator, market_nonce_le_8]`,
 * so the same `(creator, marketNonce)` pair always resolves to the same
 * market — useful for idempotent retry within a session.
 *
 * Wraps IDL instructions `create_market_shell` / `init_outcome` /
 * `finalize_market` (Anchor exposes them camelCased on `program.methods`).
 */

import { type Connection, PublicKey, SYSVAR_RENT_PUBKEY } from '@solana/web3.js';
import type BN from 'bn.js';
import { estimateComputeUnits } from '../constants/cu-table.js';
import {
  ASSOCIATED_TOKEN_PROGRAM_ID,
  MORFI_OPINION_PROGRAM_ID,
  SYSTEM_PROGRAM_ID,
  TOKEN_PROGRAM_ID,
} from '../constants/programs.js';
import { getProgram } from '../idl/loader.js';
import { deriveBcTokenVaultAta } from '../pda/ata.js';
import {
  deriveBackerFeeVaultPda,
  deriveBackerShareMintPda,
  deriveBcQuoteVaultPda,
  deriveBondingCurvePda,
  deriveClaimRightsMintPda,
  deriveMarketPda,
  deriveOutcomePda,
  derivePlatformFeeVaultPda,
  derivePrizePoolVaultPda,
  deriveTokenMetadataPda,
} from '../pda/derivations.js';
import type { BuildParams, BuildResult } from '../types/build-result.js';
import { createBuildResult, invokeMethod, uniqueSignerKeys } from './common.js';

/** MPL Token Metadata program id (mainnet + devnet — same on both clusters). */
const MPL_TOKEN_METADATA_PROGRAM_ID: PublicKey = new PublicKey(
  'metaqbxxUerdq28cj1RbAWkYQm3ybzjb6a8bt518x1s',
);

export interface CreateMarketShellParams extends BuildParams {
  /** User wallet pubkey — becomes the on-chain `market.creator` and must sign the tx. */
  creator: PublicKey;
  /** Rent/metadata sponsor signer. Defaults to `creator` for legacy/self-funded creation. */
  payer?: PublicKey;
  /** PlatformConfig PDA-derived `market_config` singleton for this network. */
  marketConfig: PublicKey;
  /** Fresh u64 nonce — the orchestrator picks this randomly per attempt. */
  marketNonce: BN;
  /** Number of outcomes the market will have. Caller must call `buildInitOutcome` exactly this many times. */
  nOutcomes: number;
  /** Market ticker (Metaplex name field constraints apply — typically uppercase, ≤7 chars). */
  ticker: string;
  /** Metadata URI for the market (typically an IPFS pin). */
  uri: string;
  /** Quote mint pubkey (devnet/mainnet USDC), constrained to `market_config.quote_mint`. */
  quoteMint: PublicKey;
}

export interface InitOutcomeParams extends BuildParams {
  /** User wallet pubkey — must match the `creator` used in `buildCreateMarketShell`. Signs. */
  creator: PublicKey;
  /** Rent/metadata sponsor signer. Defaults to `creator` for legacy/self-funded outcome creation. */
  payer?: PublicKey;
  /** Same `marketNonce` used in the shell so the derived market PDA matches.
   *  Optional when `market` is supplied directly — in that case the PDA
   *  derivation is skipped (used by the add-outcome-to-existing-market
   *  flow which knows the pubkey but not the nonce). */
  marketNonce?: BN;
  /** Direct market PDA pubkey. When provided, takes precedence over the
   *  `creator + marketNonce` derivation. Use this for the add-outcome flow
   *  where the market already exists and you know its address. */
  market?: PublicKey;
  /** Same `marketConfig` used in the shell. */
  marketConfig: PublicKey;
  /** Quote mint pubkey (e.g. devnet USDC). Fixed account used to initialize the BC quote vault as a real SPL Token account. */
  quoteMint: PublicKey;
  /** Outcome-mint keypair's PUBLIC key. The Keypair itself stays in caller-land — it must additionally sign the tx after building. */
  outcomeMint: PublicKey;
  /** 0-based outcome index. Must be called sequentially (0, 1, … N-1). */
  outcomeIndex: number;
  /** Metaplex token name field (≤32 bytes). */
  name: string;
  /** Metaplex token symbol field (≤10 bytes, conventionally uppercase). */
  symbol: string;
  /** Per-outcome ticker (≤7 chars conventionally). */
  ticker: string;
  /** Metadata URI for the outcome token. */
  uri: string;
}

export interface FinalizeMarketParams extends BuildParams {
  /** User wallet pubkey — same `creator` as the shell + outcomes. Signs. */
  creator: PublicKey;
  /** Same `marketNonce` used in the shell so the derived market PDA matches. */
  marketNonce: BN;
  /** Same `marketConfig` used in the shell. */
  marketConfig: PublicKey;
}

/**
 * Build the `create_market_shell` transaction. Allocates the Market account
 * and the per-market vaults/mints. Signers: `[creator, payer]` when distinct.
 *
 * @param connection - Solana RPC connection.
 * @param params - See {@link CreateMarketShellParams}.
 * @returns BuildResult — unsigned tx ready for signing.
 */
export async function buildCreateMarketShell(
  connection: Connection,
  params: CreateMarketShellParams,
): Promise<BuildResult> {
  const programId = params.programId ?? MORFI_OPINION_PROGRAM_ID;
  const program = await getProgram(connection, programId);
  const payer = params.payer ?? params.creator;

  const [market] = deriveMarketPda(params.creator, params.marketNonce, programId);
  const [backerShareMint] = deriveBackerShareMintPda(market, programId);
  const [backerFeeShareMetadata] = deriveTokenMetadataPda(backerShareMint);
  const [prizePoolVault] = derivePrizePoolVaultPda(market, programId);
  const [backerFeeVault] = deriveBackerFeeVaultPda(market, programId);
  const [platformFeeVault] = derivePlatformFeeVaultPda(params.marketConfig, programId);

  const instruction = await invokeMethod(
    program,
    'createMarketShell',
    params.nOutcomes,
    params.marketNonce,
    params.ticker,
    params.uri,
  )
    .accounts({
      creator: params.creator,
      payer,
      marketConfig: params.marketConfig,
      quoteMint: params.quoteMint,
      market,
      backerShareMint,
      backerFeeShareMetadata,
      prizePoolVault,
      backerFeeVault,
      platformFeeVault,
      systemProgram: SYSTEM_PROGRAM_ID,
      tokenProgram: TOKEN_PROGRAM_ID,
      tokenMetadataProgram: MPL_TOKEN_METADATA_PROGRAM_ID,
      rent: SYSVAR_RENT_PUBKEY,
      associatedTokenProgram: ASSOCIATED_TOKEN_PROGRAM_ID,
    })
    .instruction();

  const computeUnits = params.computeUnits ?? estimateComputeUnits('create_market_shell');
  return createBuildResult([instruction], uniqueSignerKeys(params.creator, payer), computeUnits);
}

/**
 * Build the `init_outcome` transaction for a single outcome. Must be called
 * sequentially per `outcomeIndex` (the on-chain contract rejects out-of-order
 * indices). Signers: `[creator, payer, outcomeMint]` when payer is distinct.
 *
 * The orchestrator generates a fresh outcome-mint `Keypair` for each
 * outcome and must (a) pass its public key here, (b) co-sign the tx with
 * the keypair's secret BEFORE the user signs.
 *
 * The `quoteMint` is a fixed account so the program initializes the BC quote
 * vault as a real SPL Token account during outcome creation. Without it, a
 * zero-traded loser could reach graduation with a missing quote vault.
 *
 * @param connection - Solana RPC connection.
 * @param params - See {@link InitOutcomeParams}.
 * @returns BuildResult — unsigned tx ready for signing.
 */
export async function buildInitOutcome(
  connection: Connection,
  params: InitOutcomeParams,
): Promise<BuildResult> {
  const programId = params.programId ?? MORFI_OPINION_PROGRAM_ID;
  const program = await getProgram(connection, programId);
  const payer = params.payer ?? params.creator;

  let market: PublicKey;
  if (params.market !== undefined) {
    market = params.market;
  } else if (params.marketNonce !== undefined) {
    [market] = deriveMarketPda(params.creator, params.marketNonce, programId);
  } else {
    throw new Error('buildInitOutcome: either `market` or `marketNonce` must be provided');
  }
  const [outcome] = deriveOutcomePda(market, params.outcomeIndex, programId);
  const [bondingCurve] = deriveBondingCurvePda(market, params.outcomeIndex, programId);
  const [claimRightsMint] = deriveClaimRightsMintPda(market, params.outcomeIndex, programId);
  const [bcQuoteVault] = deriveBcQuoteVaultPda(market, params.outcomeIndex, programId);
  const bcTokenVault = deriveBcTokenVaultAta(market, params.outcomeMint);
  const [outcomeMintMetadata] = deriveTokenMetadataPda(params.outcomeMint);
  const [claimRightsMintMetadata] = deriveTokenMetadataPda(claimRightsMint);

  const instruction = await invokeMethod(
    program,
    'initOutcome',
    params.outcomeIndex,
    params.name,
    params.symbol,
    params.ticker,
    params.uri,
  )
    .accounts({
      creator: params.creator,
      payer,
      market,
      marketConfig: params.marketConfig,
      outcome,
      bondingCurve,
      outcomeMint: params.outcomeMint,
      outcomeMintMetadata,
      claimRightsMint,
      claimRightsMintMetadata,
      bcTokenVault,
      bcQuoteVault,
      quoteMint: params.quoteMint,
      systemProgram: SYSTEM_PROGRAM_ID,
      tokenProgram: TOKEN_PROGRAM_ID,
      tokenMetadataProgram: MPL_TOKEN_METADATA_PROGRAM_ID,
      rent: SYSVAR_RENT_PUBKEY,
      associatedTokenProgram: ASSOCIATED_TOKEN_PROGRAM_ID,
    })
    .instruction();

  const computeUnits = params.computeUnits ?? estimateComputeUnits('init_outcome');
  return createBuildResult(
    [instruction],
    uniqueSignerKeys(params.creator, payer, params.outcomeMint),
    computeUnits,
  );
}

/**
 * Build the `finalize_market` transaction. Transitions the Market from
 * `Created` → `Backing` after every outcome has been initialized. Signers:
 * `[creator]`. Idempotent at the protocol level — calling twice errors.
 *
 * @param connection - Solana RPC connection.
 * @param params - See {@link FinalizeMarketParams}.
 * @returns BuildResult — unsigned tx ready for signing.
 */
export async function buildFinalizeMarket(
  connection: Connection,
  params: FinalizeMarketParams,
): Promise<BuildResult> {
  const programId = params.programId ?? MORFI_OPINION_PROGRAM_ID;
  const program = await getProgram(connection, programId);

  const [market] = deriveMarketPda(params.creator, params.marketNonce, programId);

  const instruction = await invokeMethod(program, 'finalizeMarket')
    .accounts({
      creator: params.creator,
      market,
      marketConfig: params.marketConfig,
    })
    .instruction();

  const computeUnits = params.computeUnits ?? estimateComputeUnits('finalize_market');
  return createBuildResult([instruction], [params.creator], computeUnits);
}
